import { getVersionUpgrade, VersionUpgrade } from '@pancakeswap/token-lists'
import { acceptListUpdate, updateListVersion, useFetchListCallback } from '@pancakeswap/token-lists/react'
import { EXCHANGE_PAGE_PATHS } from 'config/constants/exchange'
import { UNSUPPORTED_LIST_URLS } from 'config/constants/lists'
import { usePublicClient } from 'wagmi'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useAtomValue } from 'jotai'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { useAllLists } from 'state/lists/hooks'
import useSWRImmutable from 'swr/immutable'
import { useActiveListUrls } from './hooks'
import { inactiveListsWantedAtom, listsToFetch } from './lazy'
import { useListState, useListStateReady, initialState } from './lists'

export default function Updater(): null {
  const { chainId } = useActiveChainId()
  const provider = usePublicClient({ chainId })

  const [listState, dispatch] = useListState()
  const router = useRouter()
  const includeListUpdater = useMemo(() => {
    return EXCHANGE_PAGE_PATHS.some((item) => {
      return router.pathname.startsWith(item)
    })
  }, [router.pathname])

  const isReady = useListStateReady()

  // get all loaded lists, and the active urls
  const lists = useAllLists()
  const activeListUrls = useActiveListUrls()
  // the picker typed a search: the inactive lists are wanted now (state/lists/lazy.ts)
  const wantInactive = useAtomValue(inactiveListsWantedAtom)

  useEffect(() => {
    if (isReady) {
      dispatch(updateListVersion())
    }
  }, [dispatch, isReady])

  const fetchList = useFetchListCallback(dispatch)

  // whenever a list is not loaded and not loading, try again to load it: the active lists on load, the inactive ones once a search asks
  useSWRImmutable(isReady && ['first-fetch-token-list', lists, wantInactive], () => {
    listsToFetch(lists, activeListUrls ?? [], wantInactive).forEach((listUrl) => {
      fetchList(listUrl).catch((error) => console.debug('list added fetching error', error))
    })
  })

  useSWRImmutable(
    includeListUpdater && isReady && listState !== initialState ? ['token-list'] : null,
    async () => {
      // refresh what is in use: the active lists and any inactive list already loaded (never a first fetch of a 2.5 MB list)
      return Promise.all(
        Object.keys(lists)
          .filter((url) => activeListUrls?.includes(url) || lists[url]?.current)
          .map((url) => fetchList(url).catch((error) => console.debug('interval list fetching error', error))),
      )
    },
    {
      dedupingInterval: 1000 * 60 * 10,
      refreshInterval: 1000 * 60 * 10,
    },
  )

  // if any lists from unsupported lists are loaded, check them too (in case new updates since last visit)
  useEffect(() => {
    if (isReady) {
      Object.keys(UNSUPPORTED_LIST_URLS).forEach((listUrl) => {
        const list = lists[listUrl]
        if (!list || (!list.current && !list.loadingRequestId && !list.error)) {
          fetchList(listUrl).catch((error) => console.debug('list added fetching error', error))
        }
      })
    }
  }, [fetchList, provider, lists, isReady])

  // automatically update lists if versions are minor/patch
  useEffect(() => {
    if (!isReady) return
    Object.keys(lists).forEach((listUrl) => {
      const list = lists[listUrl]
      if (list.current && list.pendingUpdate) {
        const bump = getVersionUpgrade(list.current.version, list.pendingUpdate.version)
        // eslint-disable-next-line default-case
        switch (bump) {
          case VersionUpgrade.NONE:
            throw new Error('unexpected no version bump')
          // update any active or inactive lists
          case VersionUpgrade.PATCH:
          case VersionUpgrade.MINOR:
          case VersionUpgrade.MAJOR:
            dispatch(acceptListUpdate(listUrl))
        }
      }
    })
  }, [dispatch, lists, activeListUrls, isReady])

  return null
}
